// ── Polymarket ticker parser ────────────────────────────────────
//
// Polymarket tickers reach g1 in one of two shapes:
//   1. Hydra-synthesized: `PM-{sport}-{away}-{home}-{YYYY-MM-DD}{suffix}`
//      Suffixes: `''` (moneyline), `-spread-{home|away}-{N}pt{N}`, or
//      `-total-{N}pt{N}`. `pt` is Polymarket's slug-safe stand-in for `.`.
//   2. Raw CLOB condition id: `0x{64-hex}`. We have no structural info
//      from the string alone — the human-readable label has to come from
//      somewhere else (HydraMarket question, API-fetched title map, etc.).
//
// `parsePolymarketTicker` produces the same `ParsedTicker` shape kalshi
// uses so the rest of the label layer (describeMarket, displayTitle,
// TickerDisplay) can branch on `category === 'polymarket'` without
// caring about the underlying ticker format.
//
// `detectVenue` is the prefix-based discriminator used by `parseTicker`
// in parse.ts to dispatch between this and the kalshi parser.

import type { ParsedTicker, PlayerInfo } from './types';
import {
  classifyPolyMlbExtendedSuffix,
  titleCasePolySlug,
} from './polyPropsEvents';
import { MLB_PROP_STAT_LABELS } from './sports/mlb';

const PM_REGEX =
  /^PM-([a-z]+)-([a-z0-9]+)-([a-z0-9]+)-(\d{4}-\d{2}-\d{2})(.*)$/i;

// Exact full-game line suffixes only, end-anchored: period-scoped lines
// (`-1h-spread-…`, `-f5-total-…`, half totals) deliberately stay unmatched —
// they carry a period this ParsedTicker shape can't express, and the live
// hydra frame (marketFrameNormalization) classifies them with it. The sign is
// defensive: no producer emits one today (hydra prefixes raw Gamma slugs and
// every line-token parser matches unsigned), but one must survive unchanged.
const PM_SPREAD_SUFFIX = /^-spread-(home|away)-(-?\d+(?:pt\d+)?)$/i;
const PM_TOTAL_SUFFIX = /^-total-(-?\d+(?:pt\d+)?)$/i;

const SOCCER_PM_SPORTS = new Set(['fif', 'fifwc']);

function parsePolyLineToken(token: string): number | null {
  const value = Number(token.toLowerCase().replace('pt', '.'));
  return Number.isFinite(value) ? value : null;
}

export function detectVenue(ticker: string): 'kalshi' | 'polymarket' {
  const t = String(ticker ?? '').trim();
  if (!t) return 'kalshi';
  if (t.startsWith('PM-') || t.startsWith('pm-')) return 'polymarket';
  if (t.toLowerCase().startsWith('0x')) return 'polymarket';
  return 'kalshi';
}

function truncatePolyId(raw: string): string {
  if (raw.length <= 16) return raw;
  return `${raw.slice(0, 8)}…${raw.slice(-4)}`;
}

export function parsePolymarketTicker(ticker: string): ParsedTicker {
  const raw = String(ticker ?? '').trim();

  const match = raw.match(PM_REGEX);
  if (!match) {
    // Raw condition id (0x…) or any other non-PM shape. We can't derive
    // teams/sport/date from the string; consumers should rely on an
    // external title (API-fetched, HydraMarket question, etc.).
    const display = truncatePolyId(raw);
    return {
      raw,
      category: 'polymarket',
      series: 'PM',
      date: null,
      game: null,
      player: null,
      spread: null,
      total: null,
      tennis: null,
      outcome: null,
      shortLabel: display,
      fullLabel: `Polymarket ${display}`,
    };
  }

  const [, sportRaw, awayRaw, homeRaw, date, suffix] = match;
  const sport = SOCCER_PM_SPORTS.has(sportRaw.toLowerCase())
    ? 'SOCCER'
    : sportRaw.toUpperCase();
  const awayCode = awayRaw.toUpperCase();
  const homeCode = homeRaw.toUpperCase();

  // MLB Poly props/events (poly-props-events, 2026-07-16): `-nrfi`,
  // `-extra-innings`, `-{hr|k}-{player}-{XptY}` ride the same PM game stem.
  // Handle them BEFORE the generic outcome regex — `-nrfi` matches it and
  // would label as "NRFI wins", which misreads the polarity (Poly YES on the
  // -nrfi slug = a run IS scored — YRFI, same as Kalshi's KXMLBRFI YES).
  const mlbExt =
    sport === 'MLB' ? classifyPolyMlbExtendedSuffix(suffix.toLowerCase()) : null;
  if (mlbExt) {
    const matchup = `${awayCode} @ ${homeCode}`;
    if (mlbExt.kind === 'prop') {
      const statLabel = MLB_PROP_STAT_LABELS[mlbExt.stat];
      const playerName = titleCasePolySlug(mlbExt.playerSlug);
      // Team code isn't in the Poly slug — PlayerInfo carries the display
      // name only; describeMarket's poly branch reads `outcome`.
      const player: PlayerInfo = {
        teamCode: '',
        playerId: mlbExt.playerSlug,
        displayName: playerName,
      };
      return {
        raw,
        category: 'polymarket',
        series: `PM-${sport}`,
        date,
        game: { awayCode, homeCode },
        player,
        spread: null,
        total: null,
        tennis: null,
        outcome: `${playerName} ${mlbExt.threshold}+ ${statLabel}`,
        shortLabel: `${statLabel} ${mlbExt.threshold}+: ${playerName}`,
        fullLabel: `${matchup} (Poly MLB) · ${playerName} ${mlbExt.threshold}+ ${statLabel}`,
      };
    }
    const isRfi = mlbExt.kind === 'rfi';
    const betLabel = isRfi ? '1st Inning Run (YRFI)' : 'Extra Innings';
    return {
      raw,
      category: 'polymarket',
      series: `PM-${sport}`,
      date,
      game: { awayCode, homeCode },
      player: null,
      spread: null,
      total: null,
      tennis: null,
      outcome: isRfi ? 'YRFI' : 'EXTRAS',
      shortLabel: `${isRfi ? 'YRFI' : 'Extra Innings'}: ${matchup}`,
      fullLabel: `${matchup} (Poly MLB) · ${betLabel}`,
    };
  }

  const spreadMatch = suffix.match(PM_SPREAD_SUFFIX);
  const spreadValue = spreadMatch ? parsePolyLineToken(spreadMatch[2]) : null;
  // Poly labels SOCCER spreads with the FIRST slug team as "home" (neutral-
  // venue convention) — the inverse of g1's first=away. Mirror the frame
  // parser's verified-live flip (marketFrameNormalization.ts: SCO/MAR
  // inverted, BOS/SEA aligned) so this fallback identity can't contradict the
  // canonical frame.
  const spreadRole = spreadMatch ? spreadMatch[1].toLowerCase() : null;
  const spreadRoleIsHome =
    sport === 'SOCCER' ? spreadRole === 'away' : spreadRole === 'home';
  const spread =
    spreadMatch && spreadValue !== null
      ? {
          favoredTeam: spreadRoleIsHome ? homeCode : awayCode,
          spreadValue,
          side: 'cover' as const,
        }
      : null;

  const totalMatch = suffix.match(PM_TOTAL_SUFFIX);
  const totalValue = totalMatch ? parsePolyLineToken(totalMatch[1]) : null;
  const total =
    totalMatch && totalValue !== null
      ? { totalValue, side: 'over' as const }
      : null;

  const outcomeRaw = suffix.match(/^-(?:moneyline-)?([a-z0-9]+)$/i)?.[1] ?? null;
  const outcome =
    outcomeRaw == null
      ? null
      : outcomeRaw.toLowerCase() === 'draw'
        ? 'DRAW'
        : outcomeRaw.toUpperCase();
  const outcomeLabel =
    outcome === 'DRAW' ? 'Draw' : outcome ? `${outcome} wins` : null;
  const short = `${awayCode} @ ${homeCode}`;
  const full = `${awayCode} @ ${homeCode} (Poly ${sport})${outcomeLabel ? ` · ${outcomeLabel}` : ''}`;

  return {
    raw,
    category: 'polymarket',
    // `series` is overloaded across parsers; for poly we stash the
    // PM-{SPORT} stem here so describeMarket can recover the sport label
    // without re-parsing the raw ticker.
    series: `PM-${sport}`,
    date,
    game: { awayCode, homeCode },
    player: null,
    spread,
    total,
    tennis: null,
    outcome,
    shortLabel: short,
    fullLabel: full,
  };
}
